/**
 * PR-C Problem 1 — Variant detection from two independent signals + voting.
 *
 * Root cause (2026-06-05 OCR test, 16k-line broadcast log): the live-scan
 * pipeline reads dex numbers and region names correctly but NEVER targets the
 * bottom variant-tab pill, so the variant always fell through to "pokemon".
 * The Live Scan diagnostic confidently reported `VARIANT: pokemon` on a clearly
 * Lucky-themed screen.
 *
 * Fix: two independent signals, vote between them, never silently default to
 * "pokemon".
 *
 *   Signal A — bottom-tab-pill ROI OCR + fuzzy lexicon match against the seven
 *     known labels (POKÉMON / SHINY / LUCKY / XXL / XXS / PERFECT). The native
 *     layer crops the bottom ~12% pill band and OCRs it; we accept a result
 *     only if it fuzzy-matches a known label (rejects POKEDEX, partials, etc.).
 *
 *   Signal B — screen-tint colour heuristic. The native layer samples the
 *     dominant hue/saturation of the upper 60% of the frame (the dex grid). We
 *     map that to a variant (Lucky = warm orange, Perfect = green, etc.).
 *
 * Voting (per brief §"Voting"):
 *   - A and B agree              → high confidence (≥0.9)
 *   - disagree                   → trust A (tab OCR) at moderate (~0.7), log it
 *   - A empty, B clear           → moderate (~0.65)
 *   - both inconclusive          → keep last-known (sticky) at low confidence;
 *                                   NEVER silently default to "pokemon"
 *
 * Pure, side-effect-free, no DOM — unit-testable with `npx tsx`.
 */

import type { PokedexVariant } from "./pogoPokedexVariantClassifier";

/** The seven variant tabs the bottom pill bar can show. */
export type TabVariant = "pokemon" | "shiny" | "lucky" | "xxl" | "xxs" | "perfect";

export const TAB_VARIANTS: readonly TabVariant[] = [
  "pokemon",
  "shiny",
  "lucky",
  "xxl",
  "xxs",
  "perfect",
];

/**
 * Canonical label strings (uppercased, accent-stripped) per tab variant.
 *
 * GPT-5 N2 — Shadow and Purified are intentionally NOT in this lexicon. The
 * cascade classifier does not track them as MAIN-dex variants, so there is no
 * tab to resolve them to. Leaving them out means a Shadow/Purified pill simply
 * fails the fuzzy gate (Signal A returns null) and voting falls back to the
 * sticky last-known variant — or "unresolved" if there is none — rather than
 * being forced into a wrong tab. That fail-open behaviour is the safe mode (we
 * never silently mislabel), so this omission is deliberate, not an oversight.
 */
const TAB_LABELS: Record<TabVariant, string[]> = {
  pokemon: ["POKEMON"],
  shiny: ["SHINY"],
  lucky: ["LUCKY"],
  xxl: ["XXL"],
  xxs: ["XXS"],
  // No bare "100": the pill reads "★ 100%", and a bare 100 is a dex number or
  // progress count — it flipped frames into the Perfect dex (2026-08-30 heal).
  perfect: ["PERFECT", "100%"],
};

/** Normalise OCR text: uppercase, strip accents, keep A-Z0-9% only. */
export function normaliseLabel(raw: string): string {
  return raw
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "") // strip combining accents (É → E)
    .toUpperCase()
    .replace(/[^A-Z0-9%]/g, "");
}

/**
 * Levenshtein edit distance (small inputs only — variant labels are ≤7 chars).
 */
export function editDistance(a: string, b: string): number {
  if (a === b) return 0;
  if (a.length === 0) return b.length;
  if (b.length === 0) return a.length;
  let prev = new Array(b.length + 1);
  for (let j = 0; j <= b.length; j++) prev[j] = j;
  for (let i = 1; i <= a.length; i++) {
    const cur = [i];
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + cost);
    }
    prev = cur;
  }
  return prev[b.length];
}

export interface FuzzyMatch {
  variant: TabVariant;
  /** Normalised label that matched. */
  label: string;
  /** 0..1 similarity (1 = exact). */
  similarity: number;
}

/**
 * Fuzzy-match a single OCR token against the known tab labels.
 *
 * A token matches a label when its edit distance is within an
 * allowance that scales with label length:
 *   - exact match → similarity 1
 *   - short codes (≤3 chars: XXL/XXS/100) require an EXACT match (allowance 0),
 *     so an OCR slip like XXC does NOT match XXL (GPT-5 N1)
 *   - longer labels allow distance ≤ ceil(len/4)
 *
 * Returns the best match or null. Designed so reasonable misreads of the
 * real labels match (LUCKY≈LUCKY, SHIN→SHINY) while non-labels do NOT
 * (POKEDEX must not match POKEMON; SHIN alone must not match SHINY when it
 * is too short relative to allowance — see tests).
 */
export function fuzzyMatchTabLabel(token: string): FuzzyMatch | null {
  const norm = normaliseLabel(token);
  if (norm.length === 0) return null;

  let best: FuzzyMatch | null = null;
  for (const variant of TAB_VARIANTS) {
    for (const label of TAB_LABELS[variant]) {
      // Length guard. For multi-char word labels we require the token to be the
      // SAME length (substitutions/transpositions only) — a token shorter or
      // longer is a truncation/run-on, not a misread, so e.g. "SHIN" (4) is
      // rejected against "SHINY" (5) but "LUKCY"/"SHINV" (5) still match. Short
      // codes (XXL/XXS/100/100%) keep an exact-length requirement too.
      if (norm.length !== label.length) continue;
      const dist = editDistance(norm, label);
      // GPT-5 N1: short codes (≤3 chars: XXL/XXS/100) require an EXACT match.
      // A 1-edit allowance let an OCR slip like XXC match XXL at sim 0.67 — too
      // little signal to disambiguate 3-char codes safely. Length ≥4 keeps the
      // ceil(len/4) allowance so reasonable misreads of word labels still match.
      const allowance = label.length <= 3 ? 0 : Math.ceil(label.length / 4);
      if (dist > allowance) continue;
      const similarity = 1 - dist / Math.max(norm.length, label.length);
      if (!best || similarity > best.similarity) {
        best = { variant, label, similarity };
      }
    }
  }
  return best;
}

export interface TabPillSignal {
  variant: TabVariant | null;
  /** 0..1 from fuzzy similarity, attenuated when no high-confidence pill. */
  confidence: number;
  matchedLabel: string | null;
}

export interface TabPillLine {
  text: string;
  /** OCR confidence 0..1 for this line (the active pill renders sharper). */
  confidence: number;
}

/**
 * Signal A — resolve the active variant from OCR'd bottom-pill lines.
 *
 * The active pill renders with an orange highlight and crisper text, so when
 * several pills OCR we prefer the highest-OCR-confidence line. We accept only
 * fuzzy-validated labels; everything else is dropped (the lexicon gate).
 */
export function resolveTabPillSignal(lines: readonly TabPillLine[]): TabPillSignal {
  let best: { variant: TabVariant; score: number; label: string } | null = null;
  for (const line of lines) {
    const m = fuzzyMatchTabLabel(line.text);
    if (!m) continue;
    // Combine OCR sharpness with lexicon similarity — the highlighted (active)
    // pill scores highest on both.
    const score = m.similarity * Math.max(0.1, line.confidence);
    if (!best || score > best.score) {
      best = { variant: m.variant, score, label: m.label };
    }
  }
  if (!best) return { variant: null, confidence: 0, matchedLabel: null };
  return {
    variant: best.variant,
    confidence: Math.min(1, best.score),
    matchedLabel: best.label,
  };
}

export interface TintSample {
  /** Dominant hue 0..360 of the upper-60% dex grid area. */
  hue: number | null;
  /** Saturation 0..1. */
  saturation: number | null;
  /** Value/brightness 0..1. */
  value?: number | null;
  /**
   * High-frequency white-speckle ratio 0..1 (shiny sparkle). Optional; when
   * present and high it is a strong Shiny signal independent of hue.
   */
  speckle?: number | null;
}

export interface TintSignal {
  variant: TabVariant | null;
  confidence: number;
}

/**
 * Signal B — tint is CORROBORATION / VETO ONLY (consolidation PR, 2026-06-19).
 *
 * It used to positively SELECT a variant from hue (Lucky=orange, Perfect=GREEN,
 * Pokémon=yellow/cream). That table was WRONG (Perfect is red/salmon/peach, not
 * green — see the verified colour→family map in pogoPokedexVariantClassifier's
 * COLOUR_FAMILY_ALLOWED_VARIANTS) and it let tint independently overwrite a
 * confirmed chip-text read (the 64× `hue:xxl:0.65` Galar false positives).
 *
 * Per the unified authority chain (variantResolution.resolveVariant), tint can
 * only CORROBORATE chip-text or feed the colour-impossibility VETO — it can
 * NEVER select. So this function no longer returns a positively-selected
 * variant; it always returns `{ variant: null, confidence: 0 }`. The page
 * colour-family veto (the ONE colour-truth map) does all colour-based work now.
 *
 * Kept as an exported no-op (rather than deleted) so the existing two-signal
 * vote call site and its tests degrade safely to "tab-OCR only" without a
 * signature change; the positive-selection branches are intentionally gone.
 */
export function resolveTintSignal(_sample: TintSample): TintSignal {
  return { variant: null, confidence: 0 };
}

export type VariantSignalSource =
  | "tab+tint"
  | "tab-only"
  | "tint-only"
  | "sticky"
  | "unresolved"
  // colour-narrowed pill re-read (2026-06-19): the active-pill read failed
  // (source:"none") and the variant was recovered from the page COLOUR family.
  // "colour-sole": a single-variant family resolved it directly.
  // "colour-narrowed-pill": a multi-variant family narrowed a wide pill re-read
  // to exactly one allowed token.
  | "colour-sole"
  | "colour-narrowed-pill";

export interface VariantVoteInput {
  tab: TabPillSignal;
  tint: TintSignal;
  /** Last confidently-resolved variant for this session (sticky fallback). */
  lastKnown?: TabVariant | null;
}

export interface VariantVoteResult {
  variant: PokedexVariant | null;
  /** 0..1. */
  confidence: number;
  source: VariantSignalSource;
  /** True when A and B disagreed (logged for telemetry). */
  disagreement: boolean;
  /** Human-readable provenance for the diagnostic panel. */
  detail: string;
}

/** A tab signal counts as "present" only with a real fuzzy match. */
function tabPresent(t: TabPillSignal): boolean {
  return t.variant != null && t.confidence > 0;
}
function tintPresent(t: TintSignal): boolean {
  return t.variant != null && t.confidence > 0;
}

/**
 * Vote between Signal A (tab OCR) and Signal B (tint). Never silently defaults
 * to "pokemon": when both are inconclusive we fall back to the sticky
 * last-known variant (if any) at low confidence, else return null/unresolved.
 */
export function voteVariant(input: VariantVoteInput): VariantVoteResult {
  const { tab, tint, lastKnown = null } = input;
  const a = tabPresent(tab);
  const b = tintPresent(tint);

  if (a && b) {
    if (tab.variant === tint.variant) {
      return {
        variant: tab.variant,
        confidence: Math.min(0.99, 0.9 + 0.09 * tab.confidence),
        source: "tab+tint",
        disagreement: false,
        detail: `${labelFor(tab.variant)} (tab-OCR ✓ tint ✓)`,
      };
    }
    // Disagreement → trust the tab OCR at moderate confidence; log it.
    return {
      variant: tab.variant,
      confidence: 0.7,
      source: "tab-only",
      disagreement: true,
      detail: `${labelFor(tab.variant)} (tab-OCR ✓ tint≠${labelFor(tint.variant)})`,
    };
  }

  if (a && !b) {
    return {
      variant: tab.variant,
      confidence: Math.max(0.6, Math.min(0.85, 0.6 + 0.25 * tab.confidence)),
      source: "tab-only",
      disagreement: false,
      detail: `${labelFor(tab.variant)} (tab-OCR ✓)`,
    };
  }

  if (!a && b) {
    return {
      variant: tint.variant,
      confidence: 0.65,
      source: "tint-only",
      disagreement: false,
      detail: `${labelFor(tint.variant)} (tint ✓)`,
    };
  }

  // Both inconclusive — sticky, NEVER silent "pokemon".
  if (lastKnown) {
    return {
      variant: lastKnown,
      confidence: 0.3,
      source: "sticky",
      disagreement: false,
      detail: `${labelFor(lastKnown)} (sticky)`,
    };
  }
  return {
    variant: null,
    confidence: 0,
    source: "unresolved",
    disagreement: false,
    detail: "unresolved",
  };
}

function labelFor(v: TabVariant | PokedexVariant | null): string {
  if (!v) return "unresolved";
  switch (v) {
    case "pokemon": return "Main";
    case "shiny": return "Shiny";
    case "lucky": return "Lucky";
    case "xxl": return "XXL";
    case "xxs": return "XXS";
    case "perfect": return "Perfect";
    default: return String(v);
  }
}

export { labelFor as variantDisplayLabel };

/**
 * Build 8 / v1.4.6 — hue-gating state (REMOVED in the consolidation PR,
 * 2026-06-19).
 *
 * The Build-8 fix lived here as MODULE-LEVEL singletons (`_frameCounter`,
 * `_lastChipText*`) plus `tickFrame`/`updateChipTextCache`/`isHueGated`/
 * `getCachedChipTextVariant`/`resetHueGateState`/`voteVariantGated`. Two
 * sequential live-scan sessions leaked that state into each other, and it was a
 * second, parallel place where chip-text-vs-tint precedence was decided — the
 * exact drift this PR consolidates.
 *
 * It now lives as SESSION-SCOPED state in `variantResolution.createChipTextGate`
 * (same HUE_GATE_WINDOW=10 / HUE_GATE_MIN_CONFIDENCE=0.95 contract, no module
 * globals, isolated per session). Tint is corroboration/veto-only in the unified
 * chain, so the gate is now belt-and-braces rather than load-bearing. See
 * variantResolution.ts and its tests for the replacement + the no-leak test.
 */
